import type { SluggedId } from "Models";
import { leaguesV2Api } from "../leaguesV2Api";
import {
  addOptimisticTeam,
  assignPlayersToTeam,
  dropPlayersFromTeam,
  removePlayerFromTeam,
  removeTeamById,
  restorePlayerOnTeam,
} from "../optimistic";
import type {
  Clubhouse_LeagueV2TeamsListDto,
  Clubhouse_LeagueV2TeamDetailDto,
  Clubhouse_LeagueV2TeamPlayerRowDto,
  Clubhouse_PostLeagueV2Team,
  Clubhouse_LeagueV2CreateTeamResponseDto,
  Clubhouse_PostLeagueV2TeamAssignPlayers,
  Clubhouse_LeagueV2AssignPlayersResponseDto,
  Clubhouse_PostLeagueV2CompleteTeam,
  Clubhouse_LeagueV2CompleteTeamResponseDto,
} from "../../types/leagueTeams";

type LeagueScopedArgs = { facilityId: string; leagueId: string };
/**
 * Team and player ids are whatever the API returned. The DTOs declare them as
 * numbers, but the service sends opaque strings ("Wkp2zapg"), and these only
 * ever travel back out in a URL or a body — so they are passed through rather
 * than coerced.
 */
type ApiId = string | number;
type TeamScopedArgs = LeagueScopedArgs & { teamId: ApiId };
type CreateTeamArgs = LeagueScopedArgs & { body: Clubhouse_PostLeagueV2Team };
type RenameTeamArgs = TeamScopedArgs & { body: Clubhouse_PostLeagueV2Team };
type AssignPlayersArgs = TeamScopedArgs & {
  body: Clubhouse_PostLeagueV2TeamAssignPlayers;
};
type RemovePlayerArgs = TeamScopedArgs & { userId: ApiId };
type AddCompleteTeamArgs = LeagueScopedArgs & {
  body: Clubhouse_PostLeagueV2CompleteTeam;
};

const teamBase = ({ facilityId, leagueId }: LeagueScopedArgs) =>
  `/api/facility/${facilityId}/leagues/${leagueId}/teams`;

/**
 * A write that changes who is on a team changes the roster too: the roster row
 * carries `teamId`, and the launch-block alert reads it as the source of truth
 * for whether a league can start rounds. Invalidating only LeagueTeam leaves
 * the roster cache holding the pre-write assignment, so an operator who assigns
 * the named player still sees the alert telling them to assign that player —
 * and removing someone from a team leaves it falsely clear.
 *
 * The mirror of `ROSTER_AND_TEAMS` in roster.ts, which already does this in the
 * other direction.
 */
const TEAMS_AND_ROSTER = ["LeagueTeam", "LeagueRoster"] as const;

/**
 * Queries first, in an injection of their own — see the same split in
 * roster.ts. The mutations update this cache by hand, and
 * `updateQueryData` types its endpoint name against the api it is called
 * on, so that api has to already know `getLeagueTeams` exists.
 */
const teamsQueries = leaguesV2Api.injectEndpoints({
  endpoints: (build) => ({
    getLeagueTeams: build.query<
      Clubhouse_LeagueV2TeamsListDto,
      LeagueScopedArgs
    >({
      query: (args) => teamBase(args),
      providesTags: ["LeagueTeam"],
    }),

    getLeagueTeamDetail: build.query<
      Clubhouse_LeagueV2TeamDetailDto,
      TeamScopedArgs
    >({
      query: ({ teamId, ...args }) => `${teamBase(args)}/${teamId}`,
      providesTags: ["LeagueTeam"],
    }),
  }),
});

const teamsEndpoints = teamsQueries.injectEndpoints({
  endpoints: (build) => ({
    createLeagueTeam: build.mutation<
      Clubhouse_LeagueV2CreateTeamResponseDto,
      CreateTeamArgs
    >({
      query: ({ body, ...args }) => ({
        url: teamBase(args),
        method: "POST",
        body,
      }),
      invalidatesTags: ["LeagueTeam"],
      // Same reasoning as assignTeamPlayers: without this the new team only
      // appears once the POST and the invalidation refetch both round-trip,
      // which reads as a dropped click on a slow connection. The create
      // response only ever carries the new id (no name, no slots), so the
      // optimistic row is built from what was actually sent and a temporary
      // id; the refetch above replaces it with the server's row regardless
      // of whether that id ever gets reconciled in between.
      async onQueryStarted({ body, ...args }, { dispatch, queryFulfilled }) {
        const tempId = `optimistic-${Date.now()}-${Math.random()}` as SluggedId;
        dispatch(
          teamsQueries.util.updateQueryData(
            "getLeagueTeams",
            { facilityId: args.facilityId, leagueId: args.leagueId },
            (draft) =>
              addOptimisticTeam(draft, {
                teamId: tempId,
                teamName: body.teamName,
                players: [],
              }),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          // By the placeholder id, not `patch.undo()`: this mutation
          // invalidates, so another team created alongside it can refetch and
          // reorder the list before this one fails, and a positional undo
          // would drop whichever team now sits at the captured index.
          dispatch(
            teamsQueries.util.updateQueryData(
              "getLeagueTeams",
              { facilityId: args.facilityId, leagueId: args.leagueId },
              (draft) => removeTeamById(draft, tempId),
            ),
          );
        }
      },
    }),

    /**
     * The one way a finalized team league gains full-time players. The server
     * writes the roster rows, the team, its membership and round participation
     * in a single transaction and rolls all of it back if any part would leave
     * the league structurally invalid, so there is deliberately no optimistic
     * patch here — a half-drawn team is exactly what this endpoint exists to
     * prevent. The tags cover all three things that change: the roster rows,
     * the team board, and the per-round participant lists.
     */
    addCompleteLeagueTeam: build.mutation<
      Clubhouse_LeagueV2CompleteTeamResponseDto,
      AddCompleteTeamArgs
    >({
      query: ({ body, ...args }) => ({
        url: `${teamBase(args)}/complete`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { leagueId }) => [
        ...TEAMS_AND_ROSTER,
        { type: "League" as const, id: leagueId },
      ],
    }),

    renameLeagueTeam: build.mutation<void, RenameTeamArgs>({
      query: ({ teamId, body, ...args }) => ({
        url: `${teamBase(args)}/${teamId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["LeagueTeam"],
    }),

    withdrawLeagueTeam: build.mutation<void, TeamScopedArgs>({
      query: ({ teamId, ...args }) => ({
        url: `${teamBase(args)}/${teamId}/withdraw`,
        method: "POST",
      }),
      invalidatesTags: [
        ...TEAMS_AND_ROSTER,
        "LeagueRound",
        "Standings",
      ],
    }),

    deleteLeagueTeam: build.mutation<void, TeamScopedArgs>({
      query: ({ teamId, ...args }) => ({
        url: `${teamBase(args)}/${teamId}`,
        method: "DELETE",
      }),
      // Deleting a team drops its members (confirmed with the backend
      // 2026-09-25), so their roster rows lose their teamId.
      invalidatesTags: TEAMS_AND_ROSTER,
    }),

    assignTeamPlayers: build.mutation<
      Clubhouse_LeagueV2AssignPlayersResponseDto,
      AssignPlayersArgs
    >({
      query: ({ teamId, body, ...args }) => ({
        url: `${teamBase(args)}/${teamId}/players`,
        method: "POST",
        body,
      }),
      invalidatesTags: TEAMS_AND_ROSTER,
      // Without this, a drop sits in the rail — dnd-kit resets the chip to
      // its pre-drag position the instant it lands, since the board's own
      // data still says "unassigned" — for however long the write and the
      // invalidation refetch take. Patching the cache here moves it onto the
      // team immediately; an outright failure is then corrected by the undo
      // below.
      //
      // A 200 doesn't mean every id landed: the endpoint reports per-player,
      // and a player the server refuses (team already full, already on
      // another team) comes back in `rejected` on an otherwise-successful
      // response, which the `catch` below never sees. Left alone, that
      // player stays drawn on the team — visibly contradicting the toast
      // `TeamsTab` shows for the same rejection — until the invalidation
      // refetch happens to land, so the success branch below takes them
      // back out itself rather than trusting that timing.
      async onQueryStarted(
        { teamId, body, ...args },
        { dispatch, queryFulfilled },
      ) {
        // What this call actually drew on, so a failure can take out exactly
        // that and nothing else. `patch.undo()` cannot: its inverse removes
        // the array index captured here, and this mutation invalidates, so a
        // concurrent assignment that succeeds first will have refetched and
        // reordered the list — the undo would then delete whichever teammate
        // now sits in that slot, one whose own write had already landed.
        let inserted: (string | number)[] = [];
        dispatch(
          teamsQueries.util.updateQueryData(
            "getLeagueTeams",
            { facilityId: args.facilityId, leagueId: args.leagueId },
            (draft) => {
              inserted = assignPlayersToTeam(draft, teamId, body.userIds);
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          // Ids are opaque strings in practice despite the DTOs' numeric
          // declarations (see `ApiId`, above) — `rejected` is typed
          // `number[]`, so this compares by string rather than trusting the
          // two sides to already be the same JS type.
          const rejected = data?.rejected;
          if (rejected?.length) {
            dispatch(
              teamsQueries.util.updateQueryData(
                "getLeagueTeams",
                { facilityId: args.facilityId, leagueId: args.leagueId },
                (draft) => dropPlayersFromTeam(draft, teamId, rejected),
              ),
            );
          }
        } catch {
          if (inserted.length === 0) return;
          dispatch(
            teamsQueries.util.updateQueryData(
              "getLeagueTeams",
              { facilityId: args.facilityId, leagueId: args.leagueId },
              (draft) => dropPlayersFromTeam(draft, teamId, inserted),
            ),
          );
        }
      },
    }),

    removeTeamPlayer: build.mutation<void, RemovePlayerArgs>({
      query: ({ teamId, userId, ...args }) => ({
        url: `${teamBase(args)}/${teamId}/players/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: TEAMS_AND_ROSTER,
      // Mirror of assignTeamPlayers's patch, the other direction: without
      // this the player stays on the team card — and out of the unassigned
      // rail — until the DELETE and its refetch both round-trip.
      //
      // Reinserts on failure rather than `patch.undo()`: undo replays the
      // whole-array snapshot captured at dispatch time, and the per-player
      // pending-id gating this board relies on means a *different* player's
      // removal from the same team can succeed while this one is still in
      // flight. If this one then fails, undoing to the stale snapshot would
      // put that already-gone sibling back. Re-adding just this one player,
      // computed against whatever the list looks like when the failure
      // actually lands, can't clobber a change made in between.
      async onQueryStarted(
        { teamId, userId, ...args },
        { dispatch, queryFulfilled },
      ) {
        let removedPlayer: Clubhouse_LeagueV2TeamPlayerRowDto | undefined;
        dispatch(
          teamsQueries.util.updateQueryData(
            "getLeagueTeams",
            { facilityId: args.facilityId, leagueId: args.leagueId },
            (draft) => {
              removedPlayer = removePlayerFromTeam(draft, teamId, userId);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          if (!removedPlayer) return;
          dispatch(
            teamsQueries.util.updateQueryData(
              "getLeagueTeams",
              { facilityId: args.facilityId, leagueId: args.leagueId },
              (draft) => restorePlayerOnTeam(draft, teamId, removedPlayer!),
            ),
          );
        }
      },
    }),
  }),
});

export const {
  useGetLeagueTeamsQuery,
  useGetLeagueTeamDetailQuery,
  useLazyGetLeagueTeamDetailQuery,
  useCreateLeagueTeamMutation,
  useAddCompleteLeagueTeamMutation,
  useRenameLeagueTeamMutation,
  useDeleteLeagueTeamMutation,
  useWithdrawLeagueTeamMutation,
  useAssignTeamPlayersMutation,
  useRemoveTeamPlayerMutation,
} = teamsEndpoints;
